import { Plus, TrashIcon } from 'assets/icons';
import Button from 'components/button';
import PanelCard, { SECTION_TITLE_CLASS } from 'components/card/PanelCard';
import { formatEuros } from 'components/invoiceList/invoiceAmount';
import LastUpdateIcon from 'components/lastUpdateIcon/LastUpdateIcon';
import UseModal from 'components/modal/useModal';
import RemunerationRow from 'components/remuneration/RemunerationRow';
import Tooltip from 'components/tooltips/Tooltips';
import { formatBonusValue } from 'helper/bonusValue';
import { REMUNERATION_SECTIONS, applyRemunerationReturn, isRemunerationReturn } from 'helper/remunerationReturn';
import { mostRecent } from 'helper/lastUpdate';
import useCardFlash, { FLASH_CLASS } from 'hooks/useCardFlash';
import UseContext from 'hooks/useContext';
import useText from 'hooks/useText';
import { useEffect } from 'react';

const isAmount = (value) =>
  value !== null &&
  value !== undefined &&
  value !== '' &&
  Number.isFinite(Number(value));

/**
 * « Avantages », as on the prototype: the meal vouchers on a line, then the
 * bonuses and allowances, each opening its editor (the bin removes it), and
 * the button that adds one. `draft`: the page's changes held back (JBWEB-1468),
 * which the meal vouchers and the bonuses join; `changedIcon(section)` marks
 * a line one of them changes.
 */
const SlideOverCompanyBonusAndCompensation = ({
  companyInfo,
  setCompanyInfo,
  onSaved,
  draft,
  changedIcon = () => null,
}) => {
  const { toggle } = UseModal();
  const { setModalData, modalDataReturn, setModalDataReturn } = UseContext();
  const { text } = useText('remuneration');
  const { flashCard, isFlashed, registerCard } = useCardFlash();
  const idCompany = companyInfo?._id;
  const bonuses = companyInfo?.bonusesAndAllowances?.bonuses ?? [];
  const ticket = companyInfo?.bonusesAndAllowances?.restaurant_ticket ?? {};
  const hasTicket = Boolean(ticket.employer_share && ticket.modality);

  const open = (type, data = {}) => {
    setModalData({ idCompany, draft, ...data });
    toggle(type);
  };
  const addBonus = () =>
    open('edit_bonuses_and_allowances', {
      configuredBonusNames: bonuses.map((bonus) => bonus.name),
    });

  // A bonus or the meal vouchers saved on their own; the majorations' returns
  // are the page's.
  useEffect(() => {
    const saved = modalDataReturn;
    const section = REMUNERATION_SECTIONS[saved?.type];
    if ((section === 'restaurantTicket' || section === 'bonuses') && isRemunerationReturn(saved)) {
      setCompanyInfo((previous) => applyRemunerationReturn(previous, saved));
      // Flashed on what appears, not on an edit or a removal.
      if (saved.type === 'restaurant_ticket_updated' || saved.type === 'bonuses_and_allowances_added') flashCard(section);
      if (saved.type === 'restaurant_ticket_updated') onSaved?.();
    }
    setModalDataReturn(null);
  }, [modalDataReturn]);

  const lastUpdates = companyInfo?.lastUpdates ?? {};
  // `payed` is a percentage of the hourly wage or an amount, as `payed_type`
  // says: formatting it as euros showed a 5 % majoration as 5,00 €.
  const bonusValue = (bonus) => {
    if (isAmount(bonus.payed)) return formatBonusValue(bonus);
    if (isAmount(bonus.percentage)) return `+${bonus.percentage} %`;
    return '—';
  };

  return (
    <PanelCard
      title={text('advantagesTitle')}
      titleClassName={SECTION_TITLE_CLASS}
      collapsible
      badge={
        <LastUpdateIcon
          lastUpdate={mostRecent(
            lastUpdates.modification_ticket_restaurant ??
              lastUpdates.ajout_ticket_restaurant,
            lastUpdates.modification_prime_indemnite,
            lastUpdates.ajout_prime_indemnite,
            lastUpdates.suppression_prime_indemnite,
          )}
        />
      }
      contentClassName="py-1"
      footer={
        <Button color="SECONDARY" size="SM" onClick={addBonus}>
          <Plus wh={18} color="currentColor" />
          <span>{text('addBonus')}</span>
        </Button>
      }
    >
      <RemunerationRow
        rowRef={registerCard('restaurantTicket')}
        className={isFlashed('restaurantTicket') ? FLASH_CLASS : ''}
        label={text('ticket')}
        value={
          hasTicket && isAmount(ticket.value)
            ? formatEuros(Number(ticket.value))
            : '—'
        }
        text={
          hasTicket
            ? [
                text('ticketShare', { share: ticket.employer_share }),
                ticket.modality,
              ].join(', ')
            : text('noTicket')
        }
        onOpen={() => open('edit_bonuses_and_allowances_restaurant_ticket')}
        trailing={changedIcon('restaurantTicket')}
      />
      <div
        ref={registerCard('bonuses')}
        className={`border-t border-gray-100 py-1 ${isFlashed('bonuses') ? FLASH_CLASS : ''}`}
      >
        {!bonuses.length && (
          <p className="px-4 py-2.5 text-gray-500">{text('noBonus')}</p>
        )}
        {bonuses.map((bonus, index) => (
          <RemunerationRow
            key={bonus._id ?? index}
            className="group"
            label={index ? '' : text('bonuses')}
            value={bonusValue(bonus)}
            text={[bonus.name, bonus.unit].filter(Boolean).join(', ')}
            onOpen={() =>
              open('edit_bonuses_and_allowances', {
                idBonusesAndAllowances: bonus._id,
              })
            }
            trailing={
              <>
              {changedIcon(`bonus:${bonus._id}`)}
              <Tooltip
                text={text('removeBonus', { name: bonus.name })}
                placement="top"
              >
                <button
                  type="button"
                  aria-label={text('removeBonus', { name: bonus.name })}
                  onClick={(event) => {
                    // The row behind opens the editor.
                    event.stopPropagation();
                    open('delete_bonuses_and_allowances', {
                      idBonusesAndAllowances: bonus._id,
                    });
                  }}
                  className="flex size-7 items-center justify-center rounded-md text-gray-500 opacity-0 transition hover:bg-gray-100 hover:text-gray-900 focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <TrashIcon wh={16} color="currentColor" />
                </button>
              </Tooltip>
              </>
            }
          />
        ))}
      </div>
    </PanelCard>
  );
};

export default SlideOverCompanyBonusAndCompensation;
